import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Clock, IndianRupee, Moon, Plus, ShieldCheck, Sun, Timer, Users, Info } from 'lucide-react';

import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import {
  addLabourGangMembers,
  createLabourGang,
  createLabourShift,
  getLabourAttendancePolicy,
  getLabourGangMembers,
  getLabourGangs,
  getLabourRegister,
  getLabourShifts,
  removeLabourGangMember,
  saveLabourAttendancePolicy,
  setDefaultLabourShift
} from '../../services/api/implementation/impl';
import { Callout } from '../../../shared/components/ui/primitives';

/* Mirrors PAY_STEP_CHOICES on the backend; anything else is stored as 0. */
const PAY_STEPS = [
  { value: '0', label: 'Exact minutes' },
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' }
];

/**
 * Crews and the shifts they work.
 *
 * The group tables have existed since the foundation with nobody writing to
 * them. A supervisor thinks in crews, not forty individuals, and a night pour
 * cannot be recorded until a shift that crosses midnight exists to wrap it.
 */
const LabourGangs = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=labour' });
  const queryClient = useQueryClient();
  const { projectsForDropdown } = useProjectsList();
  const [projectId, setProjectId] = useState('');
  const [selectedGang, setSelectedGang] = useState(null);
  const [gangOpen, setGangOpen] = useState(false);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);

  const groups = useQuery({
    queryKey: ['labourGangs', projectId],
    queryFn: () => getLabourGangs(projectId ? { project_id: projectId } : {})
  });
  const shifts = useQuery({ queryKey: ['labourShifts'], queryFn: () => getLabourShifts() });
  const policy = useQuery({
    queryKey: ['labourAttendancePolicy'],
    queryFn: getLabourAttendancePolicy
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['labourGangs'] });
    queryClient.invalidateQueries({ queryKey: ['labourShifts'] });
    queryClient.invalidateQueries({ queryKey: ['labourGangMembers'] });
    queryClient.invalidateQueries({ queryKey: ['labourAttendancePolicy'] });
  };

  const saveGang = useMutation({
    mutationFn: createLabourGang,
    onSuccess: () => {
      refresh();
      setGangOpen(false);
      toast.success('Group created');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not create the group')
  });

  const saveShift = useMutation({
    mutationFn: createLabourShift,
    onSuccess: () => {
      refresh();
      setShiftOpen(false);
      toast.success('Shift created');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not create the shift')
  });

  // Which shift a punch falls into when nobody picks one. The server always
  // had this; nothing on screen could set it.
  const setDefaultShift = useMutation({
    mutationFn: setDefaultLabourShift,
    onSuccess: (_r, shiftId) => {
      queryClient.invalidateQueries({ queryKey: ['labourAttendancePolicy'] });
      toast.success(shiftId ? 'Default shift set' : 'Default shift cleared');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not change the default shift')
  });

  const savePolicy = useMutation({
    mutationFn: saveLabourAttendancePolicy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labourAttendancePolicy'] });
      toast.success('Site rule saved');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not save the rule')
  });

  const gangRows = groups.data?.data ?? [];
  const shiftRows = shifts.data?.data ?? [];
  const rules = policy.data?.data ?? {};
  const memberTotal = gangRows.reduce((sum, g) => sum + Number(g.member_count || 0), 0);
  const rulesOn = [
    rules.require_inhouse_muster_approval,
    rules.require_ot_approval,
    rules.require_photo_at_punch,
    Number(rules.advance_instalment_percent) > 0,
    rules.payment_approval_threshold != null
  ].filter(Boolean).length;

  const openMembers = (group) => {
    setSelectedGang(group);
    setMembersOpen(true);
  };

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Groups & shifts' }} />
      <div className="space-y-4 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <Callout tone="info" collapsible icon={<Info size={14} />} title="About groups &amp; shifts" className="min-w-0 lg:max-w-2xl lg:flex-1">
            Mark a whole group present in one go. A night shift (10 PM–6 AM) counts as one 8-hour day, paid like day work unless you set a night premium.
          </Callout>
          <div className="grid grid-cols-3 gap-2 sm:min-w-[360px]">
            <Stat label="Groups" value={gangRows.length} icon={<Users size={14} />} />
            <Stat label="On a crew" value={memberTotal} icon={<Users size={14} />} />
            <Stat label="Rules on" value={rulesOn} icon={<ShieldCheck size={14} />} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
              <div>
                <h2 className="font-medium text-gray-900">Groups</h2>
                <p className="text-xs text-gray-500">Crews you can punch together</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm"
                  aria-label="Project"
                >
                  <option value="">All sites</option>
                  {(projectsForDropdown ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setGangOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-medium text-white"
                >
                  <Plus size={13} /> New group
                </button>
              </div>
            </div>
            {groups.isFetching ? (
              <Loader />
            ) : gangRows.length === 0 ? (
              <Empty
                title="No groups yet"
                body="Create one, then add the crew you punch together."
              />
            ) : (
              <ul className="divide-y divide-gray-100">
                {gangRows.map((group) => (
                  <li key={group.id}>
                    <button
                      type="button"
                      onClick={() => openMembers(group)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-sm font-semibold text-brand-800">
                        {initials(group.name)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-gray-900">{group.name}</span>
                        <span className="text-xs text-gray-500">
                          Tap to add or remove workmen
                        </span>
                      </span>
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium tabular-nums text-gray-700">
                        {group.member_count} {Number(group.member_count) === 1 ? 'member' : 'members'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div>
                <h2 className="font-medium text-gray-900">Shifts</h2>
                <p className="text-xs text-gray-500">Named hours, including overnight pours</p>
              </div>
              <button
                type="button"
                onClick={() => setShiftOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800"
              >
                <Plus size={13} /> New shift
              </button>
            </div>
            {shifts.isFetching ? (
              <Loader />
            ) : shiftRows.length === 0 ? (
              <Empty
                title="No shifts"
                body="Add a night shift before recording a pour that crosses midnight."
              />
            ) : (
              <ul className="divide-y divide-gray-100">
                {shiftRows.map((shift) => {
                  const overnight = Boolean(shift.crosses_midnight);
                  const premium = Number(shift.night_premium) || 1;
                  const isDefault = Number(rules.default_shift_id) === Number(shift.id);
                  return (
                    <li key={shift.id} className="flex items-center gap-3 px-4 py-3">
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                          overnight ? 'bg-info-bg text-info-fg' : 'bg-warning-bg text-warning-fg'
                        }`}
                      >
                        {overnight ? <Moon size={16} /> : <Sun size={16} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate font-medium text-gray-900">{shift.name}</span>
                          {isDefault && (
                            <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-800">
                              Default
                            </span>
                          )}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <Clock size={12} />
                          {formatMinutes(shift.start_minutes)} – {formatMinutes(shift.end_minutes)}
                        </span>
                      </span>
                      <span className="flex flex-col items-end gap-1">
                        {overnight ? (
                          <span className="rounded-full bg-info-bg px-2 py-0.5 text-[11px] font-medium text-info-fg">
                            Overnight
                          </span>
                        ) : (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                            Same day
                          </span>
                        )}
                        <span
                          className={`text-xs tabular-nums ${
                            premium > 1 ? 'font-semibold text-brand-800' : 'text-gray-500'
                          }`}
                        >
                          {premium}× premium
                        </span>
                        <button
                          type="button"
                          disabled={setDefaultShift.isPending}
                          onClick={() => setDefaultShift.mutate(isDefault ? null : shift.id)}
                          className="text-xs font-medium text-brand-700 hover:underline disabled:opacity-50"
                        >
                          {isDefault ? 'Remove default' : 'Set as default'}
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-4 py-3">
            <h2 className="font-medium text-gray-900">Site rules</h2>
            <p className="mt-0.5 text-[13px] leading-relaxed text-gray-600">
              These apply to every workman on this site. Only the day-end is on to
              begin with — the rest stay off until you switch them on. A change
              takes effect from now on; days already saved stay exactly as they are.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
            <RuleToggle
              icon={<ShieldCheck size={16} />}
              title="Approve your own staff's marking"
              applies="Supervisors and engineers"
              body="When this is on, the days they mark wait for someone to approve them on Approvals — the same as days marked by a contractor. When it is off, what they mark counts straight away."
              checked={Boolean(rules.require_inhouse_muster_approval)}
              disabled={savePolicy.isPending}
              onChange={(checked) =>
                savePolicy.mutate({ require_inhouse_muster_approval: checked })
              }
            />
            <RuleToggle
              icon={<Timer size={16} />}
              title="Approve overtime before it is paid"
              applies="Every workman on this site"
              body="Overtime is still counted on the day it happens. Nobody is paid for it until someone releases it on Approvals."
              checked={Boolean(rules.require_ot_approval)}
              disabled={savePolicy.isPending}
              onChange={(checked) => savePolicy.mutate({ require_ot_approval: checked })}
            />
            <RuleToggle
              icon={<Camera size={16} />}
              title="Photo when punching in"
              applies="Supervisors using the app"
              body="The app will not let a group be punched in until a photo of them has been taken."
              checked={Boolean(rules.require_photo_at_punch)}
              disabled={savePolicy.isPending}
              onChange={(checked) => savePolicy.mutate({ require_photo_at_punch: checked })}
            />
            <div
              className={`md:col-span-2 flex items-start gap-3 rounded-lg border px-3 py-3 ${
                rules.auto_close_open_sessions !== false
                  ? 'border-brand-100 bg-brand-50'
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <span
                className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  rules.auto_close_open_sessions !== false
                    ? 'bg-brand-700 text-white'
                    : 'bg-white text-gray-600 ring-1 ring-gray-200'
                }`}
              >
                <Clock size={16} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-medium text-gray-900">Auto day-end</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      aria-label="Site day-end time"
                      className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm tabular-nums disabled:opacity-50"
                      key={String(rules.day_end_minutes ?? 1080)}
                      defaultValue={formatMinutes(rules.day_end_minutes ?? 1080)}
                      disabled={
                        savePolicy.isPending || rules.auto_close_open_sessions === false
                      }
                      onBlur={(e) => {
                        const mins = timeToMinutes(e.target.value);
                        if (mins === Number(rules.day_end_minutes ?? 1080)) return;
                        savePolicy.mutate({ day_end_minutes: mins });
                      }}
                    />
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-brand-700"
                      checked={rules.auto_close_open_sessions !== false}
                      disabled={savePolicy.isPending}
                      onChange={(e) =>
                        savePolicy.mutate({ auto_close_open_sessions: e.target.checked })
                      }
                    />
                  </div>
                </div>
                <p className="mt-1 text-[13px] leading-relaxed text-gray-600">
                  If nobody punches a workman out, his day stops at this time
                  instead of running all night. A workman on a night shift ends at
                  his shift&apos;s own finishing time, not this one.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-gray-600 ring-1 ring-gray-200">
                <Timer size={16} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-gray-900">Advance instalment %</p>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="w-16 rounded-lg border border-gray-300 bg-white px-2 py-1 text-right text-sm tabular-nums"
                    key={String(rules.advance_instalment_percent ?? 0)}
                    defaultValue={rules.advance_instalment_percent ?? 0}
                    disabled={savePolicy.isPending}
                    onBlur={(e) => {
                      const n = Number(e.target.value);
                      if (!Number.isFinite(n) || n === Number(rules.advance_instalment_percent)) {
                        return;
                      }
                      savePolicy.mutate({
                        advance_instalment_percent: Math.min(100, Math.max(0, n))
                      });
                    }}
                  />
                </div>
                <p className="mt-1 text-[13px] leading-relaxed text-gray-600">
                  Leave this at 0 and an advance comes off what a workman is owed
                  all at once. Put a number in and that share of each day&apos;s
                  earnings is taken back instead, until the advance is cleared. The
                  law limits how much can be taken from one day&apos;s wages, and
                  this will not go past it.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-gray-600 ring-1 ring-gray-200">
                <IndianRupee size={16} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-gray-900">Payment approval from ₹</p>
                  <input
                    type="number"
                    min="0"
                    placeholder="Off"
                    className="w-28 rounded-lg border border-gray-300 bg-white px-2 py-1 text-right text-sm tabular-nums"
                    key={String(rules.payment_approval_threshold ?? '')}
                    defaultValue={rules.payment_approval_threshold ?? ''}
                    disabled={savePolicy.isPending}
                    onBlur={(e) => {
                      const raw = e.target.value.trim();
                      const next = raw === '' ? null : Math.max(0, Number(raw));
                      const current =
                        rules.payment_approval_threshold == null
                          ? null
                          : Number(rules.payment_approval_threshold);
                      if (next !== null && !Number.isFinite(next)) {
                        return;
                      }
                      if (next === current) {
                        return;
                      }
                      savePolicy.mutate({ payment_approval_threshold: next });
                    }}
                  />
                </div>
                <p className="mt-1 text-[13px] leading-relaxed text-gray-600">
                  Leave this blank and every payment moves the balance straight
                  away. Put an amount in and any payment of that much or more
                  waits on Payment approvals until someone approves it; the
                  balance does not move before then.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-gray-600 ring-1 ring-gray-200">
                <Clock size={16} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-gray-900">Pay in steps of</p>
                  <select
                    className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm"
                    value={String(rules.pay_step_minutes ?? 0)}
                    disabled={savePolicy.isPending}
                    onChange={(e) =>
                      savePolicy.mutate({ pay_step_minutes: Number(e.target.value) })
                    }>
                    {PAY_STEPS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-[13px] leading-relaxed text-gray-600">
                  Leave this on &ldquo;Exact minutes&rdquo; and a workman is paid for every
                  minute on site. Choose 1 hour and only completed hours are paid, so
                  at ₹100 an hour the day goes up ₹100 at a time — 58 minutes pays
                  nothing, 2 hours 50 minutes pays for 2 hours. The hours shown on the
                  muster stay exact.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {gangOpen && (
        <GangModal
          projects={projectsForDropdown ?? []}
          busy={saveGang.isPending}
          onClose={() => setGangOpen(false)}
          onSubmit={(values) => saveGang.mutate(values)}
        />
      )}
      {shiftOpen && (
        <ShiftModal
          busy={saveShift.isPending}
          onClose={() => setShiftOpen(false)}
          onSubmit={(values) => saveShift.mutate(values)}
        />
      )}
      {membersOpen && selectedGang && (
        <MembersModal
          group={selectedGang}
          onClose={() => setMembersOpen(false)}
          onChanged={refresh}
        />
      )}
    </Layout>
  );
};

const Stat = ({ label, value, icon }) => (
  <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      {icon}
      {label}
    </div>
    <div className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">{value}</div>
  </div>
);

const Empty = ({ title, body }) => (
  <div className="px-4 py-10 text-center">
    <p className="text-sm font-medium text-gray-800">{title}</p>
    <p className="mt-1 text-xs text-gray-500">{body}</p>
  </div>
);

/**
 * One site rule.
 *
 * `applies` is its own line rather than a clause buried in the sentence: the
 * first question anybody asks of a rule is who it lands on, and a supervisor
 * reading these decides whether to switch one on based on whose day it changes.
 *
 * The note is 13px on gray-600, not 12px on gray-500. These are read on a site,
 * often on a phone in daylight, and they are the only explanation of what a
 * switch does — a size chosen to keep a card tidy was making the one piece of
 * text that matters the hardest thing on it to read.
 */
const RuleToggle = ({ icon, title, applies, body, checked, disabled, onChange }) => (
  <label
    className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 ${
      checked ? 'border-brand-100 bg-brand-50' : 'border-gray-200 bg-gray-50'
    } ${disabled ? 'opacity-60' : ''}`}
  >
    <span
      className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
        checked ? 'bg-brand-700 text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200'
      }`}
    >
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-gray-900">{title}</span>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
            checked ? 'bg-brand-700 text-white' : 'bg-white text-gray-500 ring-1 ring-gray-200'
          }`}
        >
          {checked ? 'On' : 'Off'}
        </span>
      </span>
      {applies ? (
        <span className="mt-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">
          Applies to {applies}
        </span>
      ) : null}
      <span className="mt-1 block text-[13px] leading-relaxed text-gray-600">{body}</span>
    </span>
    <input
      type="checkbox"
      className="sr-only"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
  </label>
);

/**
 * The trades the system recognises, in the backend's own spelling.
 *
 * Kept as a list rather than free text because the band-rate and minimum-wage
 * lookups both match on this exact string: anything typed by hand that is not
 * one of these silently matches no rate at all.
 */
const WORK_TYPES = [
  'MASON',
  'HELPER',
  'CARPENTER',
  'BAR_BENDER',
  'ELECTRICIAN',
  'PLUMBER',
  'PAINTER',
  'WELDER',
  'FITTER',
  'OPERATOR',
  'SUPERVISOR',
  'OTHER'
];

/** BAR_BENDER -> Bar bender. */
const tradeLabel = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

const GangModal = ({ projects, busy, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [workType, setWorkType] = useState('');
  return (
    <Modal name="New group" onClose={onClose} width={36}>
      <div className="space-y-3 p-1">
        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Mason crew"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Project">
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">Any site</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Work type">
          <select
            value={workType}
            onChange={(e) => setWorkType(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">Any trade</option>
            {WORK_TYPES.map((t) => (
              <option key={t} value={t}>
                {tradeLabel(t)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Narrows the list when you add people. Leave it on any trade for a
            mixed group.
          </p>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !name.trim()}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            onClick={() =>
              onSubmit({
                name: name.trim(),
                project_id: projectId || undefined,
                work_type: workType || undefined
              })
            }
          >
            {busy ? 'Saving…' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ShiftModal = ({ busy, onClose, onSubmit }) => {
  // Empty, with the example moved to a placeholder — the way the group form
  // already does it. Opening this with "Night pour" and 22:00-06:00 already
  // filled in meant anyone creating a day shift had to clear three fields
  // first, and anyone who did not notice saved a night shift by accident.
  const [name, setName] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  // 1 is genuinely the default and the label says so: it means no extra.
  const [premium, setPremium] = useState('1');
  // Only once both times are set. On an empty form the two are equal, which
  // would otherwise announce an overnight shift before anything was typed.
  const bothTimes = Boolean(start && end);
  const wraps = bothTimes && timeToMinutes(end) <= timeToMinutes(start);
  return (
    <Modal name="New shift" onClose={onClose} width={36}>
      <div className="space-y-3 p-1">
        <Field label="Name">
          <input
            value={name}
            placeholder="Night pour"
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start">
            <input
              type="time"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="End">
            <input
              type="time"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
        </div>
        <Field label="Night premium (1 = no extra)">
          <input
            type="number"
            step="0.1"
            min="1"
            value={premium}
            onChange={(e) => setPremium(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>
        <p className={`text-xs ${wraps ? 'text-info-fg' : 'text-gray-500'}`}>
          {wraps
            ? 'End is earlier than start, so this will be stored as overnight.'
            : 'If end is earlier than start, this is stored as crossing midnight.'}
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !name.trim()}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            onClick={() =>
              onSubmit({
                name: name.trim(),
                start_minutes: timeToMinutes(start),
                end_minutes: timeToMinutes(end),
                night_premium: Number(premium) || 1
              })
            }
          >
            {busy ? 'Saving…' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const MembersModal = ({ group, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const [workerId, setWorkerId] = useState('');
  const members = useQuery({
    queryKey: ['labourGangMembers', group.id],
    queryFn: () => getLabourGangMembers(group.id)
  });
  const roster = useQuery({
    queryKey: ['labourRegisterMini'],
    queryFn: () => getLabourRegister({ limit: 200 })
  });

  const add = useMutation({
    mutationFn: () => addLabourGangMembers(group.id, { worker_ids: [Number(workerId)] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labourGangMembers', group.id] });
      onChanged();
      setWorkerId('');
      toast.success('Added');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not add')
  });

  const remove = useMutation({
    mutationFn: (id) => removeLabourGangMember(group.id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labourGangMembers', group.id] });
      onChanged();
      toast.success('Removed');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not remove')
  });

  const rows = members.data?.data ?? [];
  const labour = roster.data?.data ?? roster.data ?? [];
  const list = Array.isArray(labour) ? labour : labour.rows ?? [];
  const taken = useMemo(() => new Set(rows.map((m) => Number(m.worker_id))), [rows]);
  // Narrowed to the group's trade, which is the whole point of setting one: on
  // a site with two hundred workmen, finding the six masons in one flat list is
  // the step people get wrong. A group with no work type shows everybody, and
  // so does one whose trade matches nobody — an empty picker would look broken
  // and leave no way to add the person anyway.
  const notAlreadyIn = list.filter((w) => !taken.has(Number(w.id)));
  const ofTrade = group?.work_type
    ? notAlreadyIn.filter((w) => w.trade === group.work_type)
    : notAlreadyIn;
  const available = ofTrade.length ? ofTrade : notAlreadyIn;
  const narrowed = Boolean(group?.work_type) && ofTrade.length > 0;

  return (
    <Modal name={`${group.name} — members`} onClose={onClose} width={40}>
      <div className="space-y-4 p-1">
        <div className="flex gap-2">
          <select
            value={workerId}
            onChange={(e) => setWorkerId(e.target.value)}
            className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">
              {narrowed ? `Add a ${tradeLabel(group.work_type).toLowerCase()}` : 'Add a workman'}
            </option>
            {available.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
                {w.trade ? ` · ${titleCase(w.trade)}` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!workerId || add.isPending}
            onClick={() => add.mutate()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            <Plus size={14} /> Add
          </button>
        </div>
        {members.isFetching ? (
          <Loader />
        ) : rows.length === 0 ? (
          <Empty title="Nobody in this group yet" body="Pick a workman above and add them." />
        ) : (
          <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200">
            {rows.map((m) => (
              <li key={m.worker_id} className="flex items-center gap-3 px-3 py-2.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-800">
                  {initials(m.name)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">{m.name}</span>
                  {m.trade ? (
                    <span className="mt-0.5 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                      {titleCase(m.trade)}
                    </span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="rounded-control border border-danger-border px-2.5 py-1 text-xs font-medium text-danger-fg hover:bg-danger-bg"
                  onClick={() => remove.mutate(m.worker_id)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};

const Field = ({ label, children }) => (
  <label className="block text-xs font-medium text-gray-600">
    {label}
    <div className="mt-1">{children}</div>
  </label>
);

function initials(name) {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';
}

function titleCase(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function timeToMinutes(hhmm) {
  const [h, m] = String(hhmm || '0:0').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function formatMinutes(value) {
  const n = Number(value) || 0;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default LabourGangs;
